/**
 * Pilote automatique de la sonde de mission (tests et pilote auto de dev).
 * Il utilise exactement la même interface d'entrées que le joueur.
 */
import { Vec3 } from '../core/math';
import type { Ship } from '../entities';
import type { Simulation } from '../simulation';
import { emptyInput, type PlayerInput } from './player';

export class BotPilot {
  private pressPhase = 0;

  input(sim: Simulation): PlayerInput {
    const state = sim.state;
    const input = emptyInput();
    input.skipIntermission = true;
    const player = state.player;
    // À quai : redécolle une fois la coque réparée.
    if (sim.control.mode === 'docked') {
      input.boost = player.health >= player.maxHealth * 0.99;
      return input;
    }
    if (!player.alive || sim.control.mode !== 'flight') return input;
    const w = state.weapons;
    const needsService = player.healthRatio() < 0.35 || (w.maxSwarm > 0 && w.swarm === 0 && w.missiles === 0);
    if (needsService) return this.returnToCarrier(sim, input);
    const target = this.pickTarget(sim);
    if (!target) {
      // Ramène le vaisseau vers le centre en attendant la rencontre suivante.
      this.steer(player, new Vec3(0, 0, 0), input);
      input.thrust.z = player.position.length() > 120 ? 0.6 : 0;
      return input;
    }
    const distance = target.position.distanceTo(player.position);
    const errorDeg = this.steer(player, target.position, input, target.velocity);

    // Évite de foncer dans un rocher.
    const ahead = state.collision.sphereCast(player.position, player.forward(), player.radius + 2, 80);
    if (ahead) {
      input.lookPitch = 1;
      input.thrust.y = 1;
    }
    // En position de tir, le pilote auto se stabilise (frein) pour viser ; sinon il rapproche ou recule.
    const speed = player.velocity.length();
    input.thrust.z = distance > 140 ? 1 : distance > 50 ? 0 : -0.6;
    if (distance <= 140 && speed > 25) input.brake = true;
    input.boost = distance > 300 && errorDeg < 20;

    const profile = state.data.ships.ships[state.weapons.shipId];
    this.pressPhase = (this.pressPhase + 1) % 24;
    const pulse = this.pressPhase < 4;
    const locked = state.weapons.lockState === 'locked';
    if (profile.primary === 'laser') {
      // Tir seulement si le réticule couvre réellement la cible (taille apparente).
      const angularRadius = (Math.atan2(target.radius, Math.max(distance, 1)) * 180) / Math.PI;
      input.firePrimary = errorDeg < Math.max(0.6, angularRadius * 1.1) && distance < (profile.laser?.range ?? 120) + target.radius;
      input.fireSecondary = locked && pulse;
    } else {
      input.firePrimary = locked && pulse;
      input.fireSecondary = locked && pulse && state.weapons.swarm > 0;
    }
    return input;
  }

  /** Retour au porteur : rejoint l'axe d'approche derrière la poupe, ralentit et s'aligne sur le tunnel. */
  private returnToCarrier(sim: Simulation, input: PlayerInput): PlayerInput {
    const carrier = sim.carrier;
    const player = sim.player;
    const local = carrier.toLocal(player.position);
    const approach = carrier.data.approach;
    const onAxis = Math.abs(local.x) < approach.half[0]! * 0.6 && Math.abs(local.y) < approach.half[1]! * 0.6 && local.z > carrier.data.tunnel.halfLength;
    const goal = onAxis ? carrier.tunnelEntrance() : carrier.toWorld([0, 0, approach.center[2]! + approach.half[2]! * 0.6]);
    this.steer(player, goal, input);
    const speed = player.velocity.length();
    const limit = onAxis ? carrier.data.dockMaxSpeed * 0.7 : 120;
    input.thrust.z = speed > limit ? 0 : 0.6;
    input.brake = speed > limit + 5;
    const ahead = sim.state.collision.sphereCast(player.position, player.forward(), player.radius + 2, 60);
    if (ahead && !onAxis) {
      input.lookPitch = 1;
      input.thrust.y = 1;
    }
    return input;
  }

  private pickTarget(sim: Simulation): Ship | null {
    const state = sim.state;
    const locked = state.findShip(state.weapons.lockTargetId);
    if (locked?.alive) return locked;
    let best: Ship | null = null;
    let bestDistance = Infinity;
    for (const ship of state.ships) {
      if (!ship.alive || ship.team !== 'enemy') continue;
      const d = ship.position.distanceTo(state.player.position);
      if (d < bestDistance) {
        bestDistance = d;
        best = ship;
      }
    }
    return best;
  }

  /**
   * Oriente le vaisseau vers un point via le regard analogique ; retourne l'erreur angulaire en degrés.
   * Avec la vitesse de la cible : anticipation de sa vitesse angulaire apparente (cibles en orbite, passes).
   */
  private steer(player: Ship, point: Vec3, input: PlayerInput, targetVelocity?: Vec3): number {
    const rel = point.sub(player.position);
    const local = player.rotation.conjugate().rotate(rel).normalized(new Vec3(0, 0, -1));
    const yaw = Math.atan2(local.x, -local.z);
    const pitch = Math.atan2(local.y, Math.hypot(local.x, local.z));
    let feedYaw = 0, feedPitch = 0;
    if (targetVelocity) {
      const omega = rel.cross(targetVelocity.sub(player.velocity)).scale(1 / Math.max(rel.lengthSq(), 1));
      const w = player.rotation.conjugate().rotate(omega);
      const rates = player.motor.maxRates;
      feedPitch = w.x / Math.max(rates.x, 1e-3);
      feedYaw = -w.y / Math.max(rates.y, 1e-3);
    }
    input.lookYaw = Math.max(-1, Math.min(1, yaw * 6 + feedYaw));
    input.lookPitch = Math.max(-1, Math.min(1, pitch * 6 + feedPitch));
    return (Math.acos(Math.max(-1, Math.min(1, -local.z))) * 180) / Math.PI;
  }
}
